import React, { useState, useEffect } from 'react';
import { updateCardGroup } from '../api/learning-cards';
import {
    GROUP_VISIBILITY,
    GROUP_VISIBILITY_LABELS,
    CARD_SIDE_TYPES,
    CARD_SIDE_TYPE_LABELS,
    DEFAULT_CARD_SIDE_TYPES
} from '../constants/learning-cards';

function EditGroupModal({ show, group, onClose, onGroupUpdated, onDeleteRequested }) {
    const [formData, setFormData] = useState({
        group_name: '',
        description: '',
        visibility: GROUP_VISIBILITY.PRIVATE,
        card_side_types: [...DEFAULT_CARD_SIDE_TYPES]
    });

    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (group) {
            setFormData({
                group_name: group.name || '',
                description: group.description || '',
                visibility: group.visibility || GROUP_VISIBILITY.PRIVATE,
                card_side_types: group.cardSideTypes || group.card_side_types || [...DEFAULT_CARD_SIDE_TYPES]
            });
        }
    }, [group]);

    const handleChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [id]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.group_name.trim()) {
            setError('Group name is required');
            return;
        }

        setError('');
        setLoading(true);

        try {
            const result = await updateCardGroup(group.id, formData);

            if (result.success) {
                if (onGroupUpdated) {
                    onGroupUpdated();
                }

                onClose();
            } else {
                setError(result.message || 'Failed to update group');
            }
        } catch (err) {
            setError('An unexpected error occurred. Please try again.');
            console.error('Update group error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = () => {
        if (onDeleteRequested) {
            onDeleteRequested();
        }
    };

    if (!show) return null;

    return (
        <>
            <div
                className="modal-backdrop fade show"
                style={{ zIndex: 1040 }}
                onClick={onClose}
            ></div>

            <div
                className="modal fade show d-block"
                tabIndex="-1"
                role="dialog"
                style={{ zIndex: 1050 }}
            >
                <div
                    className="modal-dialog modal-dialog-centered"
                    role="document"
                    style={{ pointerEvents: 'auto' }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">Edit Flashcard Group</h5>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={onClose}
                                aria-label="Close"
                            ></button>
                        </div>

                        <div className="modal-body">
                            {error && (
                                <div className="alert alert-danger" role="alert">
                                    {error}
                                </div>
                            )}

                            <form onSubmit={handleSubmit} noValidate>
                                <div className="mb-3">
                                    <label htmlFor="group_name" className="form-label">
                                        Group Name
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="group_name"
                                        value={formData.group_name}
                                        onChange={handleChange}
                                        disabled={loading}
                                        required
                                    />
                                </div>

                                <div className="mb-3">
                                    <label htmlFor="description" className="form-label">
                                        Description (Optional)
                                    </label>
                                    <textarea
                                        className="form-control"
                                        id="description"
                                        value={formData.description}
                                        onChange={handleChange}
                                        disabled={loading}
                                        rows="3"
                                    ></textarea>
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">Card Side Display Types</label>
                                    <div className="row">
                                        <div className="col-6">
                                            <label className="form-label small">First Side</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={CARD_SIDE_TYPE_LABELS[formData.card_side_types[0]] || 'Standard'}
                                                disabled
                                                readOnly
                                            />
                                        </div>
                                        <div className="col-6">
                                            <label className="form-label small">Second Side</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={CARD_SIDE_TYPE_LABELS[formData.card_side_types[1]] || 'Standard'}
                                                disabled
                                                readOnly
                                            />
                                        </div>
                                    </div>
                                    <div className="form-text">
                                        Card side types are set when creating the group and cannot be changed.
                                        You can choose which side shows first when starting a test.
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <label htmlFor="visibility" className="form-label">
                                        Visibility
                                    </label>
                                    <select
                                        className="form-select"
                                        id="visibility"
                                        value={formData.visibility}
                                        onChange={handleChange}
                                        disabled={loading}
                                    >
                                        {Object.entries(GROUP_VISIBILITY_LABELS).map(([value, label]) => (
                                            <option key={value} value={value}>
                                                {label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="d-flex justify-content-between align-items-center">
                                    <button
                                        type="button"
                                        className="btn btn-outline-danger"
                                        onClick={handleDeleteClick}
                                        disabled={loading}
                                    >
                                        Delete Group
                                    </button>

                                    <div className="d-flex gap-2">
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            onClick={onClose}
                                            disabled={loading}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-primary"
                                            disabled={loading}
                                        >
                                            {loading ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Updating...
                                                </>
                                            ) : (
                                                'Save Changes'
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default EditGroupModal;